import React, { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Link } from "react-router-dom";
import {
  ClassroomBoard,
  ClassroomSummary,
  MilestoneMetric,
  MyMilestone,
  createClassroom,
  createMilestone,
  deleteClassroom,
  deleteMilestone,
  fetchClassroomBoard,
  fetchClassrooms,
  fetchMyMilestones,
  joinClassroom,
  leaveClassroom,
} from "../../api";
import { tourStepsMilestones } from "./tourStepsMilestones";
import GuidedTour from "../../components/GuidedTour";
import BubbleText from "../../components/BubbleText";
import { useUserRole } from "../../context/UserRoleContext";

const METRIC_OPTIONS: {
  value: MilestoneMetric;
  label: string;
  targetLabel: string;
  defaultTarget: number;
}[] = [
  {
    value: "sessions",
    label: "Finish this many sessions",
    targetLabel: "Sessions",
    defaultTarget: 5,
  },
  {
    value: "items",
    label: "Practice this many items",
    targetLabel: "Items",
    defaultTarget: 30,
  },
  {
    value: "fast_finish",
    label: "Finish a session in this many seconds or less",
    targetLabel: "Seconds",
    defaultTarget: 60,
  },
  {
    value: "few_misses",
    label: "Finish a session with this many missed items or fewer",
    targetLabel: "Missed items",
    defaultTarget: 3,
  },
];

const buttonClass =
  "min-h-[44px] rounded border border-black px-4 py-2 text-base font-bold text-white transition-colors duration-300";

const progressPercent = (current: number, target: number) => {
  if (target <= 0) {
    return 0;
  }
  return Math.max(0, Math.min(100, Math.round((current / target) * 100)));
};

const Milestones: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { userRole } = useUserRole();
  const isTeacher = userRole === "teacher";

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [teaching, setTeaching] = useState<ClassroomSummary[]>([]);
  const [joined, setJoined] = useState<ClassroomSummary[]>([]);
  const [selectedClassroomId, setSelectedClassroomId] = useState<number | null>(
    null,
  );
  const [board, setBoard] = useState<ClassroomBoard | null>(null);
  const [myMilestones, setMyMilestones] = useState<MyMilestone[]>([]);
  const [className, setClassName] = useState("My Class");
  const [joinCode, setJoinCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [metric, setMetric] = useState<MilestoneMetric>("sessions");
  const [targetValue, setTargetValue] = useState(5);
  const [isTourRunning, setIsTourRunning] = useState(false);
  const [currentTourStep, setCurrentTourStep] = useState(0);

  const selectedMetric =
    METRIC_OPTIONS.find((option) => option.value === metric) ??
    METRIC_OPTIONS[0];

  const load = useCallback(async () => {
    setError("");
    try {
      const classrooms = await fetchClassrooms(getAccessTokenSilently);
      setTeaching(classrooms.teaching);
      setJoined(classrooms.joined);
      setSelectedClassroomId((currentId) => {
        if (
          currentId &&
          classrooms.teaching.some(
            (classroom) => classroom.classroom_id === currentId,
          )
        ) {
          return currentId;
        }
        return classrooms.teaching[0]?.classroom_id ?? null;
      });
      if (!isTeacher || classrooms.joined.length > 0) {
        const mine = await fetchMyMilestones(getAccessTokenSilently);
        setMyMilestones(mine);
      } else {
        setMyMilestones([]);
      }
    } catch (loadError) {
      console.error(loadError);
      setError("Could not load classes and milestones.");
    } finally {
      setIsLoading(false);
    }
  }, [getAccessTokenSilently, isTeacher]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!isTeacher || !selectedClassroomId) {
      setBoard(null);
      return;
    }
    const loadBoard = async () => {
      try {
        const nextBoard = await fetchClassroomBoard(
          selectedClassroomId,
          getAccessTokenSilently,
        );
        setBoard(nextBoard);
      } catch (boardError) {
        console.error(boardError);
        setError("Could not load student progress.");
      }
    };
    void loadBoard();
  }, [getAccessTokenSilently, isTeacher, selectedClassroomId]);

  useEffect(() => {
    const tourCompleted = localStorage.getItem("tourCompleted_milestones");
    if (!tourCompleted) {
      setIsTourRunning(true);
    }
  }, []);

  const handleCreateClass = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    try {
      const classroom = await createClassroom(
        className.trim() || "My Class",
        getAccessTokenSilently,
      );
      setSelectedClassroomId(classroom.classroom_id);
      setIsLoading(true);
      await load();
    } catch (createError) {
      setError(
        createError instanceof Error
          ? createError.message
          : "Could not create the class.",
      );
    }
  };

  const handleJoinClass = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    try {
      await joinClassroom(joinCode, getAccessTokenSilently);
      setJoinCode("");
      setIsLoading(true);
      await load();
    } catch (joinError) {
      setError(
        joinError instanceof Error
          ? joinError.message
          : "Could not join that class.",
      );
    }
  };

  const handleCopyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (copyError) {
      console.error(copyError);
      setError("Could not copy the class code.");
    }
  };

  const handleCreateMilestone = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedClassroomId) {
      return;
    }
    setError("");
    try {
      await createMilestone(
        selectedClassroomId,
        {
          title: title.trim(),
          description: description.trim(),
          metric,
          target_value: Number(targetValue),
        },
        getAccessTokenSilently,
      );
      setTitle("");
      setDescription("");
      if (selectedClassroomId) {
        const nextBoard = await fetchClassroomBoard(
          selectedClassroomId,
          getAccessTokenSilently,
        );
        setBoard(nextBoard);
      }
      await load();
    } catch (createError) {
      setError(
        createError instanceof Error
          ? createError.message
          : "Could not create the milestone.",
      );
    }
  };

  const handleDeleteMilestone = async (milestoneId: number) => {
    if (!window.confirm("Delete this milestone?")) {
      return;
    }
    try {
      await deleteMilestone(milestoneId, getAccessTokenSilently);
      if (selectedClassroomId) {
        const nextBoard = await fetchClassroomBoard(
          selectedClassroomId,
          getAccessTokenSilently,
        );
        setBoard(nextBoard);
      }
      await load();
    } catch (deleteError) {
      setError(
        deleteError instanceof Error
          ? deleteError.message
          : "Could not delete the milestone.",
      );
    }
  };

  const handleDeleteClass = async (classroomId: number) => {
    if (!window.confirm("Delete this class and its milestones?")) {
      return;
    }
    try {
      await deleteClassroom(classroomId, getAccessTokenSilently);
      setSelectedClassroomId(null);
      await load();
    } catch (deleteError) {
      setError(
        deleteError instanceof Error
          ? deleteError.message
          : "Could not delete the class.",
      );
    }
  };

  const handleLeaveClass = async (classroomId: number) => {
    try {
      await leaveClassroom(classroomId, getAccessTokenSilently);
      await load();
    } catch (leaveError) {
      setError(
        leaveError instanceof Error
          ? leaveError.message
          : "Could not leave the class.",
      );
    }
  };

  const selectedClassroom =
    teaching.find(
      (classroom) => classroom.classroom_id === selectedClassroomId,
    ) ?? board?.classroom;

  return (
    <div className={`page-container text-theme-text`}>
      <h1 className="milestones-heading inherit mb-8 text-3xl font-bold">
        <BubbleText>Milestones</BubbleText>
      </h1>
      <p className="inherit mb-6 max-w-3xl">
        Teachers set goals for a class. Students join with a code, practice as
        usual, and earn a badge when they hit a goal. Progress comes from
        session reports: sessions finished, items practiced, finish time, and
        missed items.
      </p>

      {error ? <p className="mb-4 text-red-600">{error}</p> : null}
      {isLoading ? <p className="inherit">Loading milestones...</p> : null}

      {isTeacher ? (
        <section className="classroom-or-join-section mb-8 w-full max-w-3xl rounded border p-4">
          <h2 className="inherit mb-3 text-2xl font-semibold">Your class</h2>
          {teaching.length === 0 ? (
            <form className="flex flex-wrap items-end gap-3" onSubmit={handleCreateClass}>
              <label className="flex flex-col">
                Class name
                <input
                  className="rounded border border-black bg-white px-3 py-1.5 text-black"
                  maxLength={50}
                  value={className}
                  onChange={(event) => setClassName(event.target.value)}
                />
              </label>
              <button
                type="submit"
                className={`${buttonClass} bg-custom-green hover:bg-custom-green-dark`}
              >
                Create class
              </button>
            </form>
          ) : (
            <>
              {teaching.length > 1 ? (
                <label className="mb-3 flex flex-col max-w-xs">
                  Class
                  <select
                    className="rounded border border-black bg-white px-3 py-1.5 text-black"
                    value={selectedClassroomId ?? ""}
                    onChange={(event) =>
                      setSelectedClassroomId(Number(event.target.value))
                    }
                  >
                    {teaching.map((classroom) => (
                      <option
                        key={classroom.classroom_id}
                        value={classroom.classroom_id}
                      >
                        {classroom.name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              {selectedClassroom?.join_code ? (
                <div className="mb-4">
                  <p className="inherit">
                    Share this code with students:{" "}
                    <span className="text-2xl font-bold tracking-widest">
                      {selectedClassroom.join_code}
                    </span>
                  </p>
                  <button
                    type="button"
                    className={`${buttonClass} bg-custom-blue hover:bg-custom-blue-dark mt-2`}
                    onClick={() =>
                      void handleCopyCode(selectedClassroom.join_code || "")
                    }
                  >
                    {copied ? "Copied" : "Copy code"}
                  </button>
                </div>
              ) : null}
              <div className="roster-section">
                <h3 className="inherit mb-2 text-xl font-semibold">Students</h3>
                {board?.students.length ? (
                  <ul className="inherit list-disc pl-5">
                    {board.students.map((student) => (
                      <li key={student.user_id}>{student.display_name}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="inherit">
                    No students yet. Ask them to open Milestones and enter the
                    class code.
                  </p>
                )}
              </div>
              <button
                type="button"
                className={`${buttonClass} bg-custom-red hover:bg-custom-red-dark mt-4`}
                onClick={() =>
                  selectedClassroomId
                    ? void handleDeleteClass(selectedClassroomId)
                    : undefined
                }
              >
                Delete class
              </button>
              <form
                className="mt-6 flex flex-wrap items-end gap-3 border-t pt-4"
                onSubmit={handleCreateClass}
              >
                <label className="flex flex-col">
                  Another class
                  <input
                    className="rounded border border-black bg-white px-3 py-1.5 text-black"
                    maxLength={50}
                    value={className}
                    onChange={(event) => setClassName(event.target.value)}
                    placeholder="Period 2"
                  />
                </label>
                <button
                  type="submit"
                  className={`${buttonClass} bg-custom-blue hover:bg-custom-blue-dark`}
                >
                  Create class
                </button>
              </form>
              <form
                className="mt-4 flex flex-wrap items-end gap-3"
                onSubmit={handleJoinClass}
              >
                <label className="flex flex-col">
                  Join another class
                  <input
                    className="rounded border border-black bg-white px-3 py-1.5 uppercase text-black"
                    maxLength={8}
                    value={joinCode}
                    onChange={(event) => setJoinCode(event.target.value)}
                    placeholder="ABC123"
                  />
                </label>
                <button
                  type="submit"
                  className={`${buttonClass} bg-custom-green hover:bg-custom-green-dark`}
                >
                  Join
                </button>
              </form>
            </>
          )}
        </section>
      ) : (
        <section className="classroom-or-join-section mb-8 w-full max-w-3xl rounded border p-4">
          <h2 className="inherit mb-3 text-2xl font-semibold">Join a class</h2>
          <form className="flex flex-wrap items-end gap-3" onSubmit={handleJoinClass}>
            <label className="flex flex-col">
              Class code
              <input
                className="rounded border border-black bg-white px-3 py-1.5 uppercase text-black"
                maxLength={8}
                value={joinCode}
                onChange={(event) => setJoinCode(event.target.value)}
                placeholder="ABC123"
              />
            </label>
            <button
              type="submit"
              className={`${buttonClass} bg-custom-green hover:bg-custom-green-dark`}
            >
              Join
            </button>
          </form>
          {joined.length > 0 ? (
            <ul className="inherit mt-4 space-y-2">
              {joined.map((classroom) => (
                <li
                  key={classroom.classroom_id}
                  className="flex flex-wrap items-center justify-between gap-2"
                >
                  <span>
                    {classroom.name}
                    {classroom.teacher_display_name
                      ? ` · ${classroom.teacher_display_name}`
                      : ""}
                  </span>
                  <button
                    type="button"
                    className={`${buttonClass} bg-custom-red hover:bg-custom-red-dark`}
                    onClick={() => void handleLeaveClass(classroom.classroom_id)}
                  >
                    Leave
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      )}

      {isTeacher ? (
        <section className="milestones-list-section w-full max-w-3xl">
          {!selectedClassroomId ? (
            <p className="inherit mb-8">
              Create a class first, then add goals for your students.
            </p>
          ) : null}
          <form
            className={`create-milestone-form mb-8 rounded border p-4 ${
              selectedClassroomId ? "" : "pointer-events-none opacity-50"
            }`}
            onSubmit={handleCreateMilestone}
          >
            <h2 className="inherit mb-3 text-2xl font-semibold">
              Add a milestone
            </h2>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex flex-col sm:col-span-2">
                Title
                <input
                  className="rounded border border-black bg-white px-3 py-1.5 text-black"
                  maxLength={50}
                  required
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder="Five sessions this week"
                />
              </label>
              <label className="flex flex-col sm:col-span-2">
                Note for students
                <input
                  className="rounded border border-black bg-white px-3 py-1.5 text-black"
                  maxLength={200}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder="Optional"
                />
              </label>
              <label className="flex flex-col sm:col-span-2">
                Goal type
                <select
                  className="rounded border border-black bg-white px-3 py-1.5 text-black"
                  value={metric}
                  onChange={(event) => {
                    const next = event.target.value as MilestoneMetric;
                    const option = METRIC_OPTIONS.find(
                      (item) => item.value === next,
                    );
                    setMetric(next);
                    if (option) {
                      setTargetValue(option.defaultTarget);
                    }
                  }}
                >
                  {METRIC_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col">
                {selectedMetric.targetLabel}
                <input
                  className="rounded border border-black bg-white px-3 py-1.5 text-black"
                  min={1}
                  required
                  type="number"
                  value={targetValue}
                  onChange={(event) =>
                    setTargetValue(Number(event.target.value))
                  }
                />
              </label>
            </div>
            <button
              type="submit"
              className={`${buttonClass} bg-custom-green hover:bg-custom-green-dark mt-4`}
            >
              Save milestone
            </button>
          </form>

          <div className="milestone-board space-y-4">
            {board?.milestones.length ? (
              board.milestones.map((milestone) => (
                <article key={milestone.milestone_id} className="rounded border p-4">
                  <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <h3 className="inherit text-xl font-semibold">
                        {milestone.title}
                      </h3>
                      {milestone.description ? (
                        <p className="inherit text-sm opacity-80">
                          {milestone.description}
                        </p>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      className={`${buttonClass} bg-custom-red hover:bg-custom-red-dark`}
                      onClick={() =>
                        void handleDeleteMilestone(milestone.milestone_id)
                      }
                    >
                      Delete
                    </button>
                  </div>
                  {milestone.students.length === 0 ? (
                    <p className="inherit text-sm">
                      Students will appear here after they join.
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {milestone.students.map((student) => (
                        <li key={student.user_id}>
                          <div className="flex flex-wrap justify-between gap-2">
                            <span>
                              {student.earned ? "✓ " : ""}
                              {student.display_name}
                            </span>
                            <span className="text-sm opacity-80">
                              {student.detail}
                            </span>
                          </div>
                          <div className="mt-1 h-2 overflow-hidden rounded bg-gray-300">
                            <div
                              className="h-full bg-custom-green"
                              style={{
                                width: `${progressPercent(
                                  student.current_value,
                                  student.progress_target,
                                )}%`,
                              }}
                            />
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </article>
              ))
            ) : (
              <p className="inherit">
                No milestones yet. Add one above after students have a goal to
                chase.
              </p>
            )}
          </div>
        </section>
      ) : null}

      {(!isTeacher || myMilestones.length > 0) && (
        <section
          className={`${isTeacher ? "mt-8" : "milestones-list-section"} w-full max-w-3xl`}
        >
          {!isTeacher ? (
            <h2 className="inherit mb-3 text-2xl font-semibold">Your goals</h2>
          ) : (
            <h2 className="inherit mb-3 text-2xl font-semibold">
              Goals from classes you joined
            </h2>
          )}
          {myMilestones.length === 0 ? (
            <p className="inherit">
              Join a class to see goals your teacher set. Earned ones also show
              on{" "}
              <Link className="underline" to="/badges-achievements">
                Badges
              </Link>
              .
            </p>
          ) : (
            <div className="space-y-4">
              {myMilestones.map((milestone) => (
                <article
                  key={milestone.milestone_id}
                  className={`rounded border p-4 ${milestone.earned ? "" : "opacity-80"}`}
                >
                  <h3 className="inherit text-xl font-semibold">
                    {milestone.earned ? "✓ " : ""}
                    {milestone.title}
                  </h3>
                  <p className="inherit text-sm opacity-80">
                    {milestone.classroom_name}
                  </p>
                  {milestone.description ? (
                    <p className="inherit mt-1">{milestone.description}</p>
                  ) : null}
                  <p className="inherit mt-2 text-sm">{milestone.detail}</p>
                  <div className="mt-2 h-2 overflow-hidden rounded bg-gray-300">
                    <div
                      className="h-full bg-custom-green"
                      style={{
                        width: `${progressPercent(
                          milestone.current_value,
                          milestone.progress_target,
                        )}%`,
                      }}
                    />
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      )}

      <GuidedTour
        steps={tourStepsMilestones()}
        isRunning={isTourRunning}
        onComplete={() => {
          setIsTourRunning(false);
          localStorage.setItem("tourCompleted_milestones", "true");
        }}
        currentStep={currentTourStep}
        onStepChange={setCurrentTourStep}
        tourName="milestones"
      />
    </div>
  );
};

export default Milestones;
